import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowRight,
    Banknote,
    BarChart3,
    Car,
    ClipboardCheck,
    Lock,
    Package,
    ShoppingCart,
    Tag,
    Users,
    Wrench,
} from 'lucide-react';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import { usePermissions } from '../../hooks/usePermissions';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import { KitList } from '../../component/common/MobileKit';
import { MobileListRow } from '../../component/common/MobileCrud';
import { KitSearch } from '../../component/common/MobileKitExtras';
import { protoIcon } from '../../component/common/ProtoIcon';
import './reports.css';

/**
 * Catálogo de relatórios do hub — permissão + rota + tom visual de cada
 * card. As chaves de permissão são exatamente as que a tela já usava antes
 * do redesign (preservadas 1:1, só a apresentação mudou): antes um card sem
 * permissão simplesmente não era renderizado; agora, seguindo
 * `prototipo-hub-relatorios.html` ("grid de cards... com gate de permissão
 * por card"), o card continua visível, mas fica travado — ícone neutro,
 * selo "Sem permissão" e botão desabilitado — em vez de desaparecer. Todos
 * os relatórios abaixo já têm rota registrada em `DefaultRouter.js`
 * (`/reports/*`), então nenhum entra como "Em breve" hoje.
 */
const REPORTS = [
    {
        key: 'sales',
        title: 'Vendas',
        description: 'Desempenho de vendas por período, forma de pagamento e categoria.',
        icon: ShoppingCart,
        tone: 'success',
        path: '/reports/sales',
        permission: 'report.sale',
    },
    {
        key: 'serviceorder',
        title: 'Ordens de Serviço',
        description: 'Volume, custos e status das ordens de serviço no período.',
        icon: Wrench,
        tone: 'secondary',
        path: '/reports/serviceorder',
        permission: 'report.serviceorder',
    },
    {
        key: 'stock',
        title: 'Estoque',
        description: 'Movimentações, entradas e saídas de estoque, incluindo o kardex por produto.',
        icon: Package,
        tone: 'brand2',
        path: '/reports/stock',
        permission: 'report.stock',
    },
    {
        key: 'products',
        title: 'Produtos',
        description: 'Rentabilidade, produtos mais relevantes e itens abaixo da margem configurada.',
        icon: Tag,
        tone: 'info',
        path: '/reports/products',
        permission: 'report.product',
    },
    {
        key: 'customers',
        title: 'Clientes',
        description: 'Total de clientes, novos, inativos, aniversariantes e histórico de vendas & OS.',
        icon: Users,
        tone: 'secondary',
        path: '/reports/customers',
        permission: 'report.customer',
    },
    {
        key: 'vehicles',
        title: 'Veículos',
        description: 'Indicadores de frota, KPIs de veículos e abas Visão Geral / Detalhado / Gráficos.',
        icon: Car,
        tone: 'error',
        path: '/reports/vehicles',
        permission: 'report.vehicle',
    },
    {
        key: 'checklist',
        title: 'Checklist',
        description: 'Filtro por nome/cliente e tabela de checklists com drill-down do histórico por ordem.',
        icon: ClipboardCheck,
        tone: 'secondary',
        path: '/reports/checklist',
        permission: 'report.checklist',
    },
    {
        key: 'inventory',
        title: 'Inventário',
        description: 'Contagem de estoque por inventário: divergências, perdas, sobras e exportação CSV/PDF.',
        icon: ClipboardCheck,
        tone: 'brand2',
        path: '/reports/inventory',
        permission: 'report.inventory',
    },
    {
        key: 'cash-flow',
        title: 'Fluxo de Caixa',
        description: 'Entradas, saídas e saldo mensal, com gráfico combinado de barras e linha de saldo.',
        icon: Banknote,
        tone: 'warning',
        path: '/reports/cash-flow',
        // Preserva a regra original: acessível por quem tem a permissão
        // específica de fluxo de caixa OU a permissão genérica de dashboard.
        permission: ['report.cashflow', 'dashboard.sale'],
    },
];

// Hub mobile (`prototipos-mobile/relatorios/hub.html`): ordem, textos, ícones e tons do protótipo.
// Cada item aponta para a mesma chave de `REPORTS` (rota e permissão continuam as de lá).
const MOBILE_HUB = [
    { key: 'sales', title: 'Vendas', sub: 'Faturamento, ticket e formas de pagamento', icon: 'receipt', tone: 'green' },
    { key: 'serviceorder', title: 'Ordens de serviço', sub: 'Status, prazos e faturamento', icon: 'orders' },
    { key: 'products', title: 'Produtos', sub: 'Mais vendidos e margem', icon: 'package', tone: 'purple' },
    { key: 'stock', title: 'Estoque', sub: 'Posição e giro', icon: 'layers', tone: 'amber' },
    { key: 'checklist', title: 'Checklists', sub: 'Conformidade das inspeções', icon: 'checks' },
    { key: 'inventory', title: 'Inventário', sub: 'Divergências de contagem', icon: 'clipcheck', tone: 'gray' },
    { key: 'cash-flow', title: 'Fluxo de caixa', sub: 'Entradas e saídas', icon: 'wallet', tone: 'green' },
    { key: 'customers', title: 'Clientes', sub: 'Ranking e recorrência', icon: 'users', tone: 'purple' },
    { key: 'vehicles', title: 'Veículos', sub: 'Atendimentos por veículo', icon: 'car' },
];

const TONE_SX = {
    success: { bgcolor: 'success.bg', color: 'success.main' },
    secondary: { bgcolor: 'secondary.bg', color: 'secondary.main' },
    brand2: { bgcolor: 'info.bg', color: 'brand2.main' },
    info: { bgcolor: 'info.bg', color: 'primary.main' },
    error: { bgcolor: 'error.bg', color: 'error.main' },
    warning: { bgcolor: 'warning.bg', color: 'warning.main' },
    locked: { bgcolor: 'background.subtle', color: 'text.disabled' },
};

// `.report-card` — ícone chip + título/descrição + botão "Acessar" (ou
// "Sem permissão" travado, quando o card está gateado) — DESIGN-SYSTEM.md
// §5.2 aplicado ao `report-card` de `prototipo-hub-relatorios.html`.
function ReportCard({ title, description, icon: Icon, tone, path, allowed }) {
    const navigate = useNavigate();
    const toneSx = allowed ? TONE_SX[tone] : TONE_SX.locked;

    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.75,
                p: 2.25,
                opacity: allowed ? 1 : 0.72,
                transition: 'transform .15s ease, box-shadow .15s ease, border-color .15s ease',
                ...(allowed && {
                    cursor: 'pointer',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: (t) => t.customShadows?.md,
                        borderColor: 'primary.main',
                    },
                }),
            }}
            onClick={allowed ? () => navigate(path) : undefined}
        >
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 }, display: 'flex', flexDirection: 'column', gap: 1.75, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ width: 34, height: 34, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, ...toneSx }}>
                        <Icon size={18} strokeWidth={2} />
                    </Box>
                    {!allowed && (
                        <Chip
                            icon={<Lock size={11} strokeWidth={2.5} />}
                            label="Sem permissão"
                            size="small"
                            sx={{ bgcolor: 'background.subtle', color: 'text.disabled', fontWeight: 700, fontSize: '11px' }}
                        />
                    )}
                </Box>

                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: '14.5px', fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                        {title}
                    </Typography>
                    <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', lineHeight: 1.5 }}>
                        {description}
                    </Typography>
                </Box>

                <Button
                    variant="outlined"
                    fullWidth
                    disabled={!allowed}
                    onClick={allowed ? () => navigate(path) : undefined}
                    startIcon={!allowed ? <Lock size={14} strokeWidth={2} /> : null}
                    endIcon={allowed ? <ArrowRight size={14} strokeWidth={2} /> : null}
                    sx={{ fontSize: '12.5px' }}
                >
                    {allowed ? 'Acessar' : 'Sem permissão'}
                </Button>
            </CardContent>
        </Card>
    );
}

export default function ReportsIndex() {
    const { hasPermission, hasAnyPermission } = usePermissions();
    const isMobileShell = useIsMobileShell();
    const navigate = useNavigate();
    const [query, setQuery] = useState('');

    const checkAllowed = (permission) =>
        Array.isArray(permission) ? hasAnyPermission(permission) : hasPermission(permission);

    if (isMobileShell) {
        const term = query.trim().toLowerCase();
        const items = MOBILE_HUB
            .filter((item) => !term || `${item.title} ${item.sub}`.toLowerCase().includes(term));
        return (
            <PageScaffold title="Relatórios" titlePageTarget="Relatórios" links={[]}>
                <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <KitSearch
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Buscar relatório…"
                        inputProps={{ 'aria-label': 'Buscar relatório', inputMode: 'search', enterKeyHint: 'search' }}
                    />
                </Box>
                <Box sx={{ mt: '16px' }} />
                <KitList>
                    {items.map((item) => {
                        const report = REPORTS.find((r) => r.key === item.key);
                        const allowed = checkAllowed(report.permission);
                        return (
                            <MobileListRow
                                key={item.key}
                                icon={protoIcon(item.icon)}
                                tone={allowed ? item.tone : 'gray'}
                                title={item.title}
                                sub={allowed ? item.sub : 'Sem permissão'}
                                chevron={allowed}
                                onClick={allowed ? () => navigate(report.path) : undefined}
                                sx={allowed ? undefined : { opacity: 0.72 }}
                            />
                        );
                    })}
                </KitList>
            </PageScaffold>
        );
    }

    return (
        <PageScaffold title="Relatórios" titlePageTarget="Relatórios" links={[]}>
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* page-head — ícone + título + descrição, igual a
                    `prototipo-hub-relatorios.html` (sem ação primária, é um hub). */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                    <Box sx={{ width: 38, height: 38, borderRadius: '11px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', background: (t) => t.palette.gradient.brand }}>
                        <BarChart3 size={18} strokeWidth={2} />
                    </Box>
                    <Box>
                        <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                            Relatórios
                        </Typography>
                        <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                            Central de relatórios do sistema — acesse indicadores de vendas, operação, cadastros e financeiro em um só lugar.
                        </Typography>
                    </Box>
                </Box>

                {/* reports-grid */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 2 }}>
                    {REPORTS.map((report) => (
                        <ReportCard
                            key={report.key}
                            title={report.title}
                            description={report.description}
                            icon={report.icon}
                            tone={report.tone}
                            path={report.path}
                            allowed={checkAllowed(report.permission)}
                        />
                    ))}
                </Box>
            </Box>
        </PageScaffold>
    );
}
